import React, { createContext, use, useCallback, useMemo, type FC, type PropsWithChildren } from "react";
import { Contacts, GQL } from "@binale-tech/shared";
import { type DataSnapshot, child } from "firebase/database";

import { CompanyContext } from "@domain/context/CompanyContext";
import { ContactUtils } from "@domain/models/utils/ContactUtils";
import { contactDelete, contactSave } from "@domain/context/mutations/contactMutations.graphql";
import { logger } from "@domain/infrastructure/logger";
import { refContacts } from "@domain/api/firebase/firebaseRootRefs";
import { useMutation } from "@apollo/client/react";
import { useCollectionListener } from "@domain/context/hooks/useCollectionListener";
import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";
import { useToastSaver } from "@ui-components/hooks/useToastSaver";

interface IContactController {
    saveContact: (contact: Contacts.Contact) => Promise<string>;
    deleteContact: (contactUuid: string) => Promise<void>;
}

interface IContactContext {
    contacts: Contacts.Contact[];
    contactsMap: Map<string, Contacts.Contact>;
}

export const ContactsContext = createContext<IContactContext>({
    contacts: [],
    contactsMap: new Map(),
});
export const ContactsControlContext = createContext<IContactController>({
    saveContact: () => Promise.reject(),
    deleteContact: () => Promise.reject(),
});

export const ContactsProvider: FC<PropsWithChildren> = ({ children }) => {
    const { companyGQL } = use(CompanyContext);
    const toastSaver = useToastSaver();

    const [contactSaveMutation] = useMutation(contactSave);
    const [contactDeleteMutation] = useMutation(contactDelete);

    const shouldSkipLoad = useMemo(
        () => !ProductAccessUtils.hasCompanyProductAccess(GQL.IProductKey.Contacts, companyGQL),
        [companyGQL],
    );
    const ref = useMemo(() => child(refContacts, String(companyGQL?.id)), [companyGQL?.id]);
    const initializer = useCallback((snap: DataSnapshot) => ContactUtils.mapContactFromGQL(snap.val()), []);

    const { list: contactsList, state: contactsMap } = useCollectionListener(ref, initializer, shouldSkipLoad);

    const contacts = useMemo(() => {
        return [...contactsList].toSorted((a, b) =>
            Contacts.getLabelName(a).toLowerCase().localeCompare(Contacts.getLabelName(b).toLowerCase()),
        );
    }, [contactsList]);

    const contactActions: IContactController = useMemo(
        () => ({
            saveContact: async (contact) => {
                const data = ContactUtils.mapContactToGQL(contact);
                logger.debug("save contact: ", { data, contact });
                const result = await toastSaver(() =>
                    contactSaveMutation({
                        variables: {
                            input: {
                                companyId: companyGQL.id,
                                contact: data,
                            },
                        },
                    }).then((res) => res.data?.contactSave),
                );
                return result;
            },
            deleteContact: async (uuid) => {
                await toastSaver(() =>
                    contactDeleteMutation({
                        variables: {
                            input: {
                                companyId: companyGQL.id,
                                id: uuid,
                            },
                        },
                    }),
                );
            },
        }),
        [companyGQL, toastSaver, contactSaveMutation, contactDeleteMutation],
    );

    const value: IContactContext = useMemo(
        () => ({
            contacts,
            contactsMap,
        }),
        [contacts, contactsMap],
    );

    return (
        <ContactsContext value={value}>
            <ContactsControlContext value={contactActions}>{children}</ContactsControlContext>
        </ContactsContext>
    );
};
